import Service, { inject } from '@ember/service';
import { setProperties, set } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { debug } from '@ember/debug';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import { UNAUTHORIZED_EVENT, beebeeAjaxGet } from 'beebee-base/lib/utility';
import moment from 'moment-timezone';
import getConfig from 'beebee-base/lib/get-config';
import { cookies } from 'beebee-base/lib/cookie';

function _applyDecoratedDescriptor(i, e, r, n, l) {
  var a = {};
  return Object.keys(n).forEach(function (i) {
    a[i] = n[i];
  }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) {
    return n(i, e, r) || r;
  }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a;
}
function _initializerDefineProperty(e, i, r, l) {
  r && Object.defineProperty(e, i, {
    enumerable: r.enumerable,
    configurable: r.configurable,
    writable: r.writable,
    value: r.initializer ? r.initializer.call(l) : void 0
  });
}

var _dec, _dec2, _dec3, _dec4, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1, _descriptor10, _descriptor11;
// ============================================================================
// COSTANTI
// ============================================================================

/** Durata default del token in secondi (12 ore) */
const DEFAULT_TOKEN_EXPIRY = 43200;

// Public, token-authenticated payment surfaces. A visitor may open these while
// logged in as a contact whose `role.redirect_page` is set (e.g. staff following
// a customer's own payment link): without the exclusion, _handleRoleRedirect /
// redirectToLogin fire `window.location.href = /<redirect_page>` on every boot,
// and since the target never matches these deep-link URLs the page reloads
// forever (observed on e-commerce `payment-request` on mobile). `pay_me` was
// already excluded for the same reason; the e-commerce `payment-request` /
// `payment_status` and booking `payment_status` routes were simply missed.
/** Route escluse dal controllo autenticazione */
const EXCLUDED_ROUTES = Object.freeze(['token_login', 'pay_me', 'payment-request', 'payment_status']);

/** Locali supportati dall'applicazione */
const SUPPORTED_LOCALES = Object.freeze(['en-US', 'en-GB', 'it-IT', 'fr-FR', 'de-DE']);

/** Locale di fallback */
const FALLBACK_LOCALE = 'it-IT';

/** Titolo default dell'applicazione */
const DEFAULT_TITLE = 'Beebeeboard';

/** Nome del cookie di sessione */
const SESSION_COOKIE_NAME = 'secure';

/** Nome del cookie del dominio */
const DOMAIN_COOKIE_NAME = 'beebee_domain';

// ============================================================================
// SERVIZIO SESSIONE
// ============================================================================

/**
 * Servizio per la gestione dell'autenticazione e della sessione utente
 * @class SessionService
 * @extends Service
 */
let SessionService = (_dec = task({
  drop: true
}), _dec2 = task({
  drop: true
}), _dec3 = task({
  drop: true
}), _dec4 = task({
  keepLatest: true
}), _class = class SessionService extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "router", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "store", _descriptor3, this);
    _initializerDefineProperty(this, "log", _descriptor4, this);
    /** Indica se il token è scaduto */
    _initializerDefineProperty(this, "tokenExpired", _descriptor5, this);
    /**
     * Solo in sviluppo: mostra la dev-toolbar di login in-app invece di fare
     * redirect alla pagina di login esterna. Sempre false in produzione.
     */
    _initializerDefineProperty(this, "showDevLogin", _descriptor6, this);
    /** Opzioni organizzazione */
    _initializerDefineProperty(this, "option_field", _descriptor7, this);
    /**
     * Locale attualmente applicato a intl e moment (lowercase).
     * Scritto solo da `applyLocale`.
     */
    _initializerDefineProperty(this, "currentLocale", _descriptor8, this);
    /** Locali di cui le traduzioni sono già state caricate (lowercase) */
    this._loadedLocales = new Set();
    /** Utente corrente */
    _initializerDefineProperty(this, "me", _descriptor9, this);
    /**
     * Snapshot protetto dei dati organization dell'utente loggato.
     * Questa copia NON vive nello store Ember Data, quindi non può essere
     * sovrascritta da pushPayload, store.push o findRecord di altri contatti.
     * Contiene: id, contact_states, product_states, event_states, project_states,
     * workhour_states, roles, categories, organization_custom_fields.
     * @type {Object|null}
     */
    _initializerDefineProperty(this, "_orgSnapshot", _descriptor0, this);
    /** Dati geolocalizzazione IP */
    this.ipLocation = null;
    /** Token di autenticazione */
    _initializerDefineProperty(this, "token", _descriptor1, this);
    /** Durata token in secondi */
    this.expires_in = DEFAULT_TOKEN_EXPIRY;
    /** Flag bootstrap completato */
    this.hasBootstrapped = false;
    /** Promise del bootstrap in corso */
    this.bootPromise = null;
    /** Permetti accesso anonimo */
    this.allowAnonymous = false;
    /** Cache dati organizzazione pubblica */
    this.openData = null;
    /** Dati gdpr dell'organizzazione */
    _initializerDefineProperty(this, "gdprField", _descriptor10, this);
    /** Record organizzazione pubblica */
    _initializerDefineProperty(this, "openOrganization", _descriptor11, this);
    /**
     * Cache degli array snapshot-backed per evitare ri-allocazioni ad ogni lettura.
     * Viene invalidata ad ogni ricostruzione di _orgSnapshot.
     * @private
     */
    this._snapshotArrayCache = new Map();
  }
  /**
   * Imposta la modalità accesso anonimo
   * @param {boolean} anonymous - True per permettere accesso anonimo
   */
  setAnonymous(anonymous = false) {
    this.allowAnonymous = anonymous;
  }

  /**
   * Imposta le opzioni dell'organizzazione
   * @param {Object|null} option_field - Opzioni organizzazione
   */
  setOption(option_field = null) {
    this.option_field = option_field;
  }

  /**
   * Imposta l'utente corrente e crea uno snapshot protetto dei dati organization.
   *
   * Lo snapshot isola contact_states, product_states e gli altri array di stato
   * dalla corruzione causata da pushPayload/store.push di altri contatti.
   * I record originali (Ember Data) restano in `me.organization` per retrocompatibilità,
   * ma tutto il codice critico (generateComputedStateProperty, collision check, ecc.)
   * legge da `_orgSnapshot`.
   *
   * @param {Object|null} me - Oggetto utente (record Contact di Ember Data)
   */
  setMe(me = null) {
    this.me = me;
    this._buildOrgSnapshot(me);
  }

  /**
   * Rebuilds the org snapshot from the current `me` record. Public hook for
   * consumers that mutate an organization hasMany locally (e.g. create/destroy
   * of organization_custom_field records) and need the snapshot-backed getters
   * (organizationCustomFields, projectStates, ...) to recompute without a
   * network reload. Reassigning the tracked `_orgSnapshot` invalidates every
   * autotracked consumer; notifyPropertyChange on the getters would not.
   */
  refreshOrgSnapshot() {
    this._buildOrgSnapshot(this.me);
  }

  /**
   * Costruisce lo snapshot protetto dell'organization a partire dal record me.
   *
   * Lo snapshot serve come INDICE di lookup: contiene solo { id } per ogni
   * record negli hasMany, così da poter verificare l'esistenza di uno state
   * anche se pushPayload corrompe organization.contact_states nello store.
   *
   * NON restituisce direttamente gli oggetti snapshot all'esterno —
   * generateComputedStateProperty usa l'indice per confermare l'esistenza
   * e poi restituisce il vero record Ember Data via store.peekRecord().
   *
   * @private
   * @param {Object|null} me - Record contact
   */
  _buildOrgSnapshot(me) {
    // Invalida la cache degli array ricostruiti: i vecchi array puntano a
    // record potenzialmente stale e un nuovo snapshot ha una composizione diversa.
    this._snapshotArrayCache = new Map();
    if (!me?.organization) {
      this._orgSnapshot = null;
      return;
    }
    const org = me.organization;

    // Helper: copia un hasMany come array leggero di { id } per lookup rapido.
    // Scarta i record senza id (nuovi non ancora salvati o in fase di destroy):
    // un id null romperebbe store.peekRecord con un'assertion.
    const snapshotIds = arr => {
      if (!Array.isArray(arr)) return [];
      return arr.filter(item => item?.id != null).map(item => ({
        id: item.id
      }));
    };
    this._orgSnapshot = {
      id: org.id,
      contact_states: snapshotIds(org.contact_states),
      product_states: snapshotIds(org.product_states),
      event_states: snapshotIds(org.event_states),
      project_states: snapshotIds(org.project_states),
      workhour_states: snapshotIds(org.workhour_states),
      payment_methods: snapshotIds(org.payment_methods),
      organization_custom_fields: snapshotIds(org.organization_custom_fields),
      categories: snapshotIds(org.categories),
      product_categories: snapshotIds(org.product_categories),
      roles: snapshotIds(org.roles)
    };
  }

  // ==========================================================================
  // API PUBBLICA — accesso agli hasMany dell'organization tramite snapshot
  // ==========================================================================

  /**
   * Restituisce un array di record Ember Data di un hasMany dell'organization,
   * risolti via peekRecord partendo dagli ID nello snapshot protetto.
   * Il risultato è immune da corruzione da pushPayload.
   *
   * @private
   * @param {string} key - nome dell'array (es. 'contact_states')
   * @returns {Array<Model>} array di record Ember Data (mai null)
   */
  _snapshotArray(key) {
    // Leggi PRIMA lo stato tracked così ogni call path lo entangla: altrimenti
    // un consumer che colpisce la cache non si invaliderebbe mai dopo un rebuild.
    const snap = this._orgSnapshot;
    const cached = this._snapshotArrayCache.get(key);
    if (cached) {
      return cached;
    }
    if (!snap?.[key]) {
      return [];
    }

    // Ricava il model type dalla chiave: 'contact_states' → 'contact_state'
    const irregularPlurals = {
      categories: 'category',
      product_categories: 'product_category'
    };
    const modelType = irregularPlurals[key] ?? key.replace(/s$/, '');
    const arr = snap[key].filter(({
      id
    }) => id != null).map(({
      id
    }) => this.store.peekRecord(modelType, id)).filter(Boolean);
    this._snapshotArrayCache.set(key, arr);
    return arr;
  }

  /**
   * Restituisce il record Ember Data con l'ID specificato nella hasMany indicata,
   * cercando prima nello snapshot per confermare che appartiene all'organization
   * dell'utente loggato.
   *
   * @private
   * @param {string} key - nome dell'array (es. 'contact_states')
   * @param {string} id - ID del record da cercare
   * @returns {Model|null}
   */
  _snapshotFindById(key, id) {
    // Ember Data ids are strings; a caller passing a number (e.g. an id read
    // from a raw JSON attr) must still match, so compare as strings.
    if (id == null) {
      return null;
    }
    const wanted = String(id);
    return this._snapshotArray(key).find(r => r.id === wanted) ?? null;
  }

  // Getter pubblici — reattivi automaticamente perché leggono _orgSnapshot (@tracked)
  get contactStates() {
    return this._snapshotArray('contact_states');
  }
  get productStates() {
    return this._snapshotArray('product_states');
  }
  get eventStates() {
    return this._snapshotArray('event_states');
  }
  get projectStates() {
    return this._snapshotArray('project_states');
  }
  get workhourStates() {
    return this._snapshotArray('workhour_states');
  }
  get roles() {
    return this._snapshotArray('roles');
  }
  get categories() {
    return this._snapshotArray('categories');
  }
  get productCategories() {
    return this._snapshotArray('product_categories');
  }
  get paymentMethods() {
    return this._snapshotArray('payment_methods');
  }
  get organizationCustomFields() {
    return this._snapshotArray('organization_custom_fields');
  }

  // Alias snake_case per retro-compatibilità con template che usavano la key snake_case
  get contact_states() {
    return this.contactStates;
  }
  get product_states() {
    return this.productStates;
  }
  get event_states() {
    return this.eventStates;
  }
  get project_states() {
    return this.projectStates;
  }
  get workhour_states() {
    return this.workhourStates;
  }
  get product_categories() {
    return this.productCategories;
  }
  get payment_methods() {
    return this.paymentMethods;
  }
  get organization_custom_fields() {
    return this.organizationCustomFields;
  }

  // Find helpers per lookup by ID
  findContactState(id) {
    return this._snapshotFindById('contact_states', id);
  }
  findProductState(id) {
    return this._snapshotFindById('product_states', id);
  }
  findEventState(id) {
    return this._snapshotFindById('event_states', id);
  }
  findProjectState(id) {
    return this._snapshotFindById('project_states', id);
  }
  findWorkhourState(id) {
    return this._snapshotFindById('workhour_states', id);
  }
  findRole(id) {
    return this._snapshotFindById('roles', id);
  }
  findCategory(id) {
    return this._snapshotFindById('categories', id);
  }
  findProductCategory(id) {
    return this._snapshotFindById('product_categories', id);
  }
  findPaymentMethod(id) {
    return this._snapshotFindById('payment_methods', id);
  }
  findOrganizationCustomField(id) {
    return this._snapshotFindById('organization_custom_fields', id);
  }

  /**
   * Imposta le opzioni dell'organizzazione
   * @param {Object|null} openOrganization - Opzioni organizzazione
   */
  setOpenOrganization(openOrganization = null) {
    this.openOrganization = openOrganization;
  }

  /**
   * Imposta i dati di geolocalizzazione IP
   * @param {Object|null} data - Dati localizzazione
   */
  setIpLocation(data = null) {
    this.ipLocation = data;
  }

  /**
   * Inizializza il servizio recuperando il token dal cookie
   */
  init() {
    super.init(...arguments);
    const accessToken = cookies.get(SESSION_COOKIE_NAME);
    if (!isEmpty(accessToken)) {
      this.authenticate(accessToken, DEFAULT_TOKEN_EXPIRY, false);
    }

    // A token that expires while the app is open answers 401 on the next
    // beebeeAjax call. That layer is a plain module with no access to this
    // service, so it announces the rejection here: end the session once and go
    // to login, instead of leaving the user on a page whose every request
    // fails.
    this._onUnauthorized = () => this.handleUnauthorized();
    window.addEventListener(UNAUTHORIZED_EVENT, this._onUnauthorized);
  }
  willDestroy() {
    window.removeEventListener(UNAUTHORIZED_EVENT, this._onUnauthorized);
    super.willDestroy(...arguments);
  }

  /**
   * Reazione a un 401 arrivato fuori dal bootstrap: il token non vale piu',
   * quindi si chiude la sessione e si va al login. Idempotente: il primo 401
   * azzera il token, quelli in coda dalle richieste parallele escono subito.
   */
  handleUnauthorized() {
    if (!this.isAuthenticated() || this.allowAnonymous) {
      return;
    }

    // Le route pubbliche (pay_me, token_login, payment_status) vivono anche
    // senza sessione valida: un 401 li' non deve buttare fuori l'utente.
    const currentRoute = this.router?.currentRouteName || '';
    if (EXCLUDED_ROUTES.some(target => currentRoute.includes(target))) {
      return;
    }
    this.tokenExpired = true;
    this.redirectToLogin();
  }

  /**
   * Verifica se l'utente è autenticato
   * @returns {boolean} True se autenticato
   */
  isAuthenticated() {
    return !isEmpty(this.token);
  }

  /**
   * Verifica se la route è esclusa dal controllo autenticazione
   * @param {Transition} transition - Transizione corrente
   * @returns {boolean} True se esclusa
   */
  isExcluded(transition) {
    if (!transition?.targetName) {
      return false;
    }
    return EXCLUDED_ROUTES.some(target => transition.targetName.includes(target));
  }

  /**
   * Autentica l'utente con un token
   * @param {string} accessToken - Token di accesso
   * @param {number} expires_in - Durata in secondi
   * @param {boolean} setCookie - Se salvare il cookie
   */
  authenticate(accessToken, expires_in = DEFAULT_TOKEN_EXPIRY, setCookie = true) {
    if (!accessToken) {
      this.logout();
      return;
    }
    this.token = accessToken;
    this.expires_in = expires_in;
    if (setCookie) {
      const expiryDays = expires_in / 86400; // secondi in un giorno
      // Secure on https (dev on http keeps working); explicit SameSite=Lax:
      // the login flow is a top-level redirect, so Lax still sends the cookie.
      cookies.set(SESSION_COOKIE_NAME, this.token, {
        expires: expiryDays,
        secure: this._location.protocol === 'https:',
        sameSite: 'Lax'
      });
    }
  }

  /**
   * Effettua il logout pulendo tutti i dati di sessione
   */
  logout() {
    setProperties(this, {
      option_field: null,
      token: null,
      bootPromise: null,
      hasBootstrapped: false,
      me: null,
      ipLocation: null,
      tokenExpired: false
    });

    // Pulisci lo snapshot org e le relative cache: su kiosk condivisi (checkin
    // booking) il logout non ricarica la pagina, quindi i getter restituirebbero
    // i dati org del precedente utente.
    this._orgSnapshot = null;
    this._snapshotArrayCache = new Map();
    this.openData = null;
    this.openOrganization = null;
    this.gdprField = null;
    document.title = DEFAULT_TITLE;
    cookies.remove(SESSION_COOKIE_NAME);
    // `beebee_domain` is written by _setupUserInterface with an explicit
    // domain scope; removing it needs the SAME domain, or the previous tenant
    // subdomain survives the logout — on a shared kiosk (no page reload) the
    // next user would inherit the prior organization.
    cookies.remove(DOMAIN_COOKIE_NAME, {
      domain: 'beebeeboard.com'
    });
  }

  /**
   * Determina il locale dell'utente
   * @returns {string} Codice locale lowercase
   */
  figureOutLocale() {
    return this.calculateLocale(this.intl.locales).toLowerCase();
  }

  /**
   * Lingua scelta dall'utente in settings/organization, se presente.
   * @returns {string|null} Codice locale lowercase
   */
  get optionLocale() {
    return this.option_field?.intl?.lang?.toLowerCase() || null;
  }

  /**
   * Unico punto in cui il locale viene applicato: intl e moment restano
   * sempre allineati. Idempotente: applicare due volte lo stesso locale
   * non ricarica le traduzioni.
   *
   * Va usato al posto di `moment.locale()` / `intl.setLocale()` diretti:
   * moment tiene un locale globale, quindi un writer fuori da qui può
   * lasciare mesi e giorni in una lingua diversa da quella dell'utente.
   *
   * @param {string} locale - Codice locale; se assente usa FALLBACK_LOCALE
   */
  *applyLocale(locale) {
    const normalized = (locale || FALLBACK_LOCALE).toLowerCase();
    if (!this._loadedLocales.has(normalized)) {
      yield this.intl.loadTranslations(normalized);
      this._loadedLocales.add(normalized);
    }
    this.intl.setLocale(normalized);
    moment.locale(normalized);
    this.currentLocale = normalized;
  }

  /**
   * Calcola il locale basandosi sulle preferenze del browser
   * @param {Array<string>} locales - Locali disponibili
   * @returns {string} Locale selezionato
   */
  calculateLocale(locales) {
    const browserLanguage = navigator.languages?.[0] || navigator.language || navigator.userLanguage;
    const normalizedLanguage = browserLanguage?.toLowerCase();
    return locales.includes(normalizedLanguage) ? browserLanguage : FALLBACK_LOCALE;
  }

  /**
   * Reindirizza alla pagina di login
   * @param {Transition|null} transition - Transizione da abortire
   * @returns {number|undefined} 0 se escluso/anonimo
   */
  redirectToLogin(transition = null) {
    if (this.isExcluded(transition) || this.allowAnonymous) {
      return 0;
    }

    // In sviluppo: mostra la dev-toolbar in-app (login + cambio org) invece
    // di reindirizzare alla pagina di login esterna. NON abortiamo la
    // transition: bootSessionTask gira nel beforeModel della application route,
    // abortire qui impedirebbe il render del template (e quindi della toolbar).
    // Produzione invariata.
    if (this.isDevelopment) {
      this.logout();
      this.showDevLogin = true;
      return;
    }
    this.logout();
    transition?.abort();
    const returnUrl = encodeURIComponent(this._location.href);
    this._location.replace(`${getConfig(this).APP.host}/../../#/?r=${returnUrl}`);
  }

  /** True quando environment === 'development' (guida la dev-toolbar) */
  get isDevelopment() {
    return getConfig(this).environment === 'development';
  }

  /** Indirection hook for testability — allows unit tests to stub location. */
  get _location() {
    return window.location;
  }

  /**
   * Rimuove il token di accesso dall'URL
   * Gestisce sia modalità hash che history
   */
  removeAccessTokenFromUrl() {
    const loc = this._location;
    const hasHashToken = loc.hash.includes('access_token');
    if (hasHashToken) {
      // Hash mode (#/): use history.replaceState instead of assigning
      // loc.hash — a hash assignment pushes a NEW history entry, leaving the
      // token-bearing URL reachable via the Back button.
      const [baseHash, queryString] = loc.hash.split('?');
      const hashParams = new URLSearchParams(queryString);
      hashParams.delete('access_token');
      const newQuery = hashParams.toString();
      const newHash = newQuery ? `${baseHash}?${newQuery}` : baseHash;
      window.history.replaceState({}, '', `${loc.href.split('#')[0]}${newHash}`);
    } else {
      // Modalità history
      const url = new URL(loc.href);
      url.searchParams.delete('access_token');
      window.history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Inizializza la sessione (singleton pattern)
   * @param {Transition} transition - Transizione corrente
   * @param {Controller} controller - Controller applicazione
   * @returns {Promise} Promise del bootstrap
   */
  async bootstrapSession(transition, controller) {
    // Già completato
    if (this.hasBootstrapped) {
      return;
    }

    // Bootstrap già in corso
    if (this.bootPromise) {
      return this.bootPromise;
    }
    this.bootPromise = this.bootSessionTask.perform(transition, controller).then(result => {
      this.hasBootstrapped = true;
      return result;
    }, error => {
      // Reset per permettere retry
      this.bootPromise = null;
      throw error;
    });
    return this.bootPromise;
  }

  /**
   * Task per inizializzazione sessione
   * Gestisce HTTPS redirect, i18n, autenticazione e setup utente
   */
  *bootSessionTask(transition, controller) {
    // Forza HTTPS in produzione: interrompe il bootstrap, altrimenti il resto
    // del task (query utente, token) continuerebbe a girare sulla pagina HTTP
    // mentre location.replace sta ancora navigando.
    if (this._enforceHttps()) {
      // location.replace() naviga in modo asincrono: questa pagina continua a
      // renderizzare finché il browser non se ne va davvero. ember-intl 8 tiene
      // il locale in `_locale`, che nasce undefined, e `t()` ci itera sopra:
      // ogni {{t}} montato in quella finestra lanciava
      // "TypeError: n is not iterable" (Sentry EMBER-MEDICAL-2X1, aperta su un
      // URL http://). Il locale è stato locale puro, nessuna I/O, quindi lo
      // seminiamo prima di uscire — il redirect resta l'unica cosa in volo.
      if (window.Intl) {
        this.intl.setLocale(SUPPORTED_LOCALES);
      }
      return;
    }

    // Carica polyfill Intl se necessario
    if (!window.Intl) {
      yield this.intl.loadPolyfill();
    }

    // Setup iniziale locale
    this.intl.setLocale(SUPPORTED_LOCALES);
    const browserLocale = this.figureOutLocale();

    // loadOpenTask porta l'option_field, che contiene la lingua scelta
    // dall'utente: serve prima di decidere quale locale applicare. Le
    // traduzioni del locale browser sono l'ipotesi più probabile, quindi
    // le carichiamo in parallelo invece di serializzare le due richieste.
    yield Promise.all([this.intl.loadTranslations(browserLocale), this.loadOpenTask.perform()]);
    this._loadedLocales.add(browserLocale);

    // Priorità: lingua scelta in settings/organization → browser → it-IT.
    // La scelta dell'utente vince sempre: applicare qui il locale browser
    // lasciava mesi e giorni in inglese anche con la lingua impostata su it-IT.
    yield* this.applyLocale(this.optionLocale || browserLocale);

    // Autenticazione da URL se presente
    const urlToken = transition.to?.queryParams?.access_token;
    if (urlToken) {
      this.authenticate(urlToken);

      // Resetta il query param sul controller per evitare che Ember lo ri-serializzi nell'URL
      if (controller) {
        set(controller, 'access_token', null);
      }
      this.removeAccessTokenFromUrl();
    }

    // Verifica autenticazione
    if (!this.isAuthenticated()) {
      this.redirectToLogin(transition);
      return;
    }

    // Carica dati utente
    yield* this._loadUserData(transition, controller);
  }

  /**
   * Forza redirect a HTTPS in produzione
   * @private
   */
  _enforceHttps() {
    const loc = this._location;
    if (loc.protocol !== 'https:' && getConfig(this).environment !== 'development') {
      loc.replace(loc.href.replace('http://', 'https://'));
      return true;
    }
    return false;
  }

  /**
   * Carica i dati dell'utente autenticato
   * @private
   */
  *_loadUserData(transition, controller) {
    try {
      const me = yield this.store.queryRecord('contact', 'me', {
        reload: true
      });
      if (!me?.id) {
        this.redirectToLogin(transition);
        return;
      }

      // Gestione redirect per ruoli specifici
      this._handleRoleRedirect(me, transition, controller);
      this.setMe(me);

      // Carica opzioni organizzazione e locale personalizzato
      yield* this._loadOrganizationOptions(me.organization?.option_field);

      // Setup UI e servizi
      this._setupUserInterface(me, controller);
    } catch (e) {
      this.log.error('Session boot error:', e);
      debug('Session boot error:', e);

      // Only a real rejection of the token justifies burning the session:
      // redirectToLogin() ends in logout(), which deletes the cookie. Failing
      // here on transient errors (LAN hiccup, backend restart, 5xx, timeout)
      // destroyed a perfectly valid cookie at every unlucky boot — perceived
      // as "the session does not survive a page reload" in dev over http.
      // The 401 the application adapter produces is an ember-data
      // UnauthorizedError built with no payload, so it carries no status at
      // all: match the error identity too, or a real rejection would be
      // mistaken for a transient failure and the expired token would survive.
      // That identity lives in `code`, NOT in `name`: ember-data builds these
      // errors with `Error.call(this, message)`, so `name` is always the
      // inherited 'Error' and only `UnauthorizedError.prototype.code` carries
      // the class. Matching on `name` never fired — an expired token stopped
      // redirecting to login and the page reloaded forever.
      const status = parseInt(e?.status ?? e?.errors?.[0]?.status, 10);
      const code = e?.code ?? e?.name;
      const isAuthRejection = status === 401 || status === 403 || code === 'UnauthorizedError' || code === 'ForbiddenError';
      if (isAuthRejection) {
        this.tokenExpired = true;
        if (!this.isExcluded(transition)) {
          this.redirectToLogin(transition);
        }
        return;
      }

      // Transient failure: keep token and cookie so the next reload can retry;
      // in development surface the toolbar to make the failure visible.
      if (this.isDevelopment) {
        this.showDevLogin = true;
      }
    }
  }
  formatIncluded(included) {
    const inc = [];
    // Guard: payloads without an `included` key must not throw, otherwise
    // loadOpenTask silently aborts and organization options never load.
    const incFirst = included?.[0];
    for (const key in incFirst) {
      if (Object.prototype.hasOwnProperty.call(incFirst, key)) {
        const value = incFirst[key];
        for (const keyV in value) {
          if (Object.prototype.hasOwnProperty.call(value, keyV)) {
            const valueV = value[keyV];
            inc.push(valueV);
          }
        }
      }
    }
    return inc;
  }

  /**
   * Task per gestire il flusso post-login inline (senza passare dal bootstrap).
   * Autentica, carica i dati utente, opzioni organizzazione e configura la UI.
   *
   * IMPORTANTE: tutta la logica post-login DEVE stare qui, non nel componente
   * chiamante. Dopo che session.me viene settato, il template potrebbe distruggere
   * il componente (es. requireLoginForBooking diventa false) e ember-concurrency
   * cancellerebbe qualsiasi task rimasto sul componente.
   *
   * @param {string} accessToken - Token di accesso OAuth
   * @param {Object} options
   * @param {Controller|null} options.controller - Controller applicazione (per setup UI)
   * @param {Object|null} options.taskAfterLogin - Task ember-concurrency da eseguire dopo il login
   */
  *afterLoginTask(accessToken, {
    controller = null,
    taskAfterLogin = null
  } = {}) {
    this.authenticate(accessToken);
    const locale = this.figureOutLocale();
    yield* this._loadUserData(null, controller, locale);

    // Gestione redirect per ruoli specifici.
    // _handleRoleRedirect in _loadUserData lo skippa quando allowAnonymous è true
    // (es. booking), quindi gestiamo il redirect qui per il caso inline login.
    const redirectPage = this.me?.role?.redirect_page;
    if (redirectPage && getConfig(this).environment !== 'development') {
      if (redirectPage.startsWith('http')) {
        /* istanbul ignore next -- window.location.href assignment can't be stubbed in Chrome (native, non-configurable); would navigate the test runner */
        window.location.href = redirectPage;
      } else if (!document.URL.includes(redirectPage)) {
        /* istanbul ignore next -- same reason: real navigation, unstubbable in this Chrome test env */
        window.location.href = `/${redirectPage}`;
      }
      return;
    }
    if (controller) {
      set(controller, 'showLogin', false);
    }
    if (taskAfterLogin) {
      taskAfterLogin.perform();
    }

    // Forza aggiornamento route per re-renderizzare i template dopo il login.
    this.router.refresh();
  }

  /**
   * Ricarica i dati dell'utente corrente, aggiorna la sessione e ritorna il record me
   * @returns {Object} Record utente aggiornato
   */
  *reloadMeTask() {
    const me = yield this.store.queryRecord('contact', 'me', {
      reload: true
    });
    this.setMe(me);
    return me;
  }

  /**
   * Carica i dati pubblici dell'organizzazione (funziona anche senza auth).
   * Aggiorna option_field, openOrganization e gdprField sulla sessione.
   * @param {boolean} force - Se true forza il ricaricamento anche se i dati sono già presenti
   */
  *loadOpenTask(force = false) {
    yield timeout(150);
    if (!force && this.openOrganization && this.option_field && this.openData) {
      return;
    }
    try {
      let open = null;
      if (typeof globalOrganizationOpen !== 'undefined') {
        open = globalOrganizationOpen;
      }
      if (open) {
        const incFormat = this.formatIncluded(open.included);
        open = {
          data: open.data,
          included: incFormat
        };
        this.openData = open;
      } else {
        const url = `${getConfig(this).APP.urlApi}/organizations/open?test=1`;
        open = yield beebeeAjaxGet(url);
        this.openData = structuredClone(open);
      }
      this.gdprField = open?.data?.relationships?.gdpr;

      // Il serializer protegge l'org del me quando arriva come sideload
      // in "included". Ma qui l'org open è il record PRINCIPALE (data).
      // Se è la stessa org del me, non pushare: i dati open sono un subset
      // (senza contact_states, product_states ecc.) e corromperebbero lo store.
      const meOrgId = this.me?.organization?.id?.toString();
      const openOrgId = open?.data?.id?.toString();
      const isSameOrg = meOrgId && openOrgId && meOrgId === openOrgId;

      // Every read above is optional-chained because /organizations/open can
      // answer without a primary record — an empty body, an error envelope, a
      // captive-portal page. `open.data.id` below was not, so that answer threw
      // inside the session bootstrap and the app came up with no shell at all.
      // Nothing in this payload can be trusted at that point: pushing it would
      // corrupt the store too. Keep the previous state and let the next call
      // retry (openOrganization stays unset, so the guard at the top re-runs).
      if (!openOrgId) {
        debug('session.loadOpenTask: /organizations/open returned no primary record; skipping');
        return;
      }
      if (!isSameOrg) {
        this.store.pushPayload(open);
      }
      this.openOrganization = this.store.peekRecord('organization', openOrgId);
      if (!this.option_field || force) {
        // Quando è la stessa org, pushPayload è stato saltato: lo store ha ancora
        // il vecchio option_field. Usiamo il valore fresco dalla risposta API.
        const optionFieldSource = isSameOrg ? open?.data?.attributes?.option_field : this.openOrganization?.option_field;
        yield* this._loadOrganizationOptions(optionFieldSource);
      }

      // Se l'organization aperta è quella del me, rigenera lo snapshot per
      // assorbire eventuali cambiamenti su hasMany (es. nuove categorie/ruoli
      // aggiunti server-side) e invalidare la cache dei getter pubblici.
      if (this.me && isSameOrg) {
        this._buildOrgSnapshot(this.me);
      }
    } catch (e) {
      this.log.error('Load open error:', e);
      debug('Load open error:', e);
    }
  }

  /**
   * Gestisce redirect basato sul ruolo utente
   * @private
   */
  _handleRoleRedirect(me, transition, controller) {
    const redirectPage = me.role?.redirect_page;
    if (!redirectPage || getConfig(this).environment === 'development' || this.allowAnonymous || this.isExcluded(transition)) {
      return;
    }
    if (redirectPage.startsWith('http')) {
      controller?.logoutProcess?.();
      return;
    }
    const currentUrl = document.URL.toLowerCase();
    const targetPage = redirectPage.toLowerCase();
    if (!currentUrl.includes(targetPage) && !this.isExcluded(transition)) {
      /* istanbul ignore next -- window.location.href assignment can't be stubbed in Chrome (native, non-configurable); would navigate the test runner */
      window.location.href = `/${targetPage}`;
    }
  }

  /**
   * Carica le opzioni dell'organizzazione e applica il locale scelto dall'utente.
   * @private
   */
  *_loadOrganizationOptions(option_field) {
    const rawOptions = option_field || '';

    // Senza option_field non c'è nulla da applicare: JSON.parse('') lancerebbe
    // e il locale già applicato dal chiamante verrebbe perso nel catch.
    if (!rawOptions) {
      return;
    }
    let optionField = null;
    try {
      optionField = JSON.parse(rawOptions);
    } catch (e) {
      this.log.error('Error parsing organization options:', e);
      debug('Error parsing organization options:', e);
      return;
    }
    this.setOption(optionField);

    // Applicare sempre: il confronto con un default hardcoded saltava
    // l'applicazione proprio quando l'utente sceglieva quel default.
    // applyLocale è idempotente, riapplicare lo stesso locale non costa nulla.
    const userLocale = optionField?.intl?.lang?.toLowerCase();
    if (userLocale) {
      yield* this.applyLocale(userLocale);
    }
  }

  /**
   * Configura l'interfaccia utente e avvia i servizi
   * @private
   */
  _setupUserInterface(me, controller) {
    // Imposta titolo pagina
    const orgName = me.organization?.name;
    document.title = orgName ? `${orgName} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
    if (!controller) {
      return;
    }

    // Configura controller (solo se content è già inizializzato)
    if (controller.content) {
      set(controller, 'content.me', me);
    }
    if (controller.startSocket && typeof controller.startSocket === 'function') {
      controller.startSocket();
    }
    if (controller.startFreshChat && controller.startFreshChat.perform) {
      controller.startFreshChat.perform(me);
    }

    // Imposta cookie dominio
    if (me.organization?.organization) {
      cookies.set(DOMAIN_COOKIE_NAME, `https://${me.organization.organization}.beebeeboard.com`, {
        domain: 'beebeeboard.com',
        secure: this._location.protocol === 'https:',
        sameSite: 'Lax'
      });
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "router", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "log", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "tokenExpired", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "showDevLogin", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "option_field", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "currentLocale", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "me", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "_orgSnapshot", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "token", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "gdprField", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "openOrganization", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "bootSessionTask", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "bootSessionTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "afterLoginTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "afterLoginTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "reloadMeTask", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "reloadMeTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "loadOpenTask", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "loadOpenTask"), _class.prototype), _class);

export { SessionService as default };
//# sourceMappingURL=session.js.map
